import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import {
 Upload, ClipboardList, AlertCircle, CheckCircle2, MessageSquare,
 Trash2, Send, Loader2, FileText, Calendar, ChevronDown, ChevronRight,
} from "lucide-react";

/**
 * InspectionTab. entry & exit condition reports + disagreement tracker.
 *
 * Designed to be dead simple:
 * 1. Upload PDF/photo of entry or exit report → AI extracts every room/item row.
 * 2. Anything Claude detected as a tenant disagreement is pre-ticked & red.
 * 3. User adds their own note, ticks any additional items they disagree with.
 * 4. One-click "Raised with agent ✓" stamps a date and starts the 7-day chase clock.
 * 5. Paste the agent's response. Tag goes green. Done.
 */
export default function InspectionTab({ caseId }) {
 const [reports, setReports] = useState([]);
 const [loading, setLoading] = useState(false);
 const [uploading, setUploading] = useState(false);
 const [uploadKind, setUploadKind] = useState("entry");
 const [reportDate, setReportDate] = useState("");
 const fileRef = useRef();

 const load = async () => {
 setLoading(true);
 try {
 const { data } = await api.get(`/cases/${caseId}/inspections`);
 setReports(data || []);
 } finally { setLoading(false); }
 };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);

 const upload = async (file) => {
 if (!file) return;
 setUploading(true);
 try {
 const fd = new FormData();
 fd.append("file", file);
 fd.append("kind", uploadKind);
 if (reportDate) fd.append("report_date", reportDate);
 await api.post(`/cases/${caseId}/inspections/extract`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 timeout: 180000,
 });
 setReportDate("");
 await load();
 } catch (e) {
 alert(e.response?.data?.detail || "Couldn't read that report. You can still add items manually.");
 } finally {
 setUploading(false);
 if (fileRef.current) fileRef.current.value = "";
 }
 };

 const deleteReport = async (id) => {
 if (!window.confirm("Delete this inspection report and all its items?")) return;
 await api.delete(`/inspections/${id}`);
 load();
 };

 // Counts across all reports
 const allItems = reports.flatMap(r => r.items || []);
 const disagreedItems = allItems.filter(i => i.tenant_disagreement);
 const needRaising = disagreedItems.filter(i => !(i.tags || []).includes("raised_with_agent"));
 const raisedNoResponse = disagreedItems.filter(i =>
 (i.tags || []).includes("raised_with_agent") && !i.agent_response && !(i.tags || []).includes("resolved")
 );

 return (
 <div className="space-y-6" data-testid="inspection-tab">
 {/* Summary banner. only when there is something to nudge about */}
 {disagreedItems.length > 0 && (
 <div className="card bg-[#FAF9F6]" data-testid="inspection-summary">
 <div className="grid sm:grid-cols-3 gap-4">
 <SummaryStat label="You disagreed with" value={disagreedItems.length} tone="navy" />
 <SummaryStat label="Still to raise with agent" value={needRaising.length} tone="red" />
 <SummaryStat label="Raised, chasing reply" value={raisedNoResponse.length} tone="amber" />
 </div>
 {(needRaising.length > 0 || raisedNoResponse.length > 0) && (
 <p className="mt-4 text-xs text-[#6B7280] font-serif italic leading-relaxed">
 {needRaising.length > 0 && <>You have <strong className="text-[#B35D4C]">{needRaising.length}</strong> item{needRaising.length === 1 ? "" : "s"} you disagreed with that haven't been raised with the agent yet. </>}
 {raisedNoResponse.length > 0 && <>And <strong className="text-[#8A6D3B]">{raisedNoResponse.length}</strong> item{raisedNoResponse.length === 1 ? " is" : "s are"} waiting on a reply (we'll nudge you once a week).</>}
 </p>
 )}
 </div>
 )}

 {/* Upload card */}
 <div className="card" data-testid="inspection-upload">
 <div className="flex items-start gap-3 mb-4">
 <ClipboardList size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div>
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Add a condition report</h3>
 <p className="text-sm text-[#6B7280] leading-relaxed">
 Upload your entry or exit condition report. We'll read every room and item and pre-flag anything where you've marked disagreement. You tick the rest.
 </p>
 </div>
 </div>

 <div className="grid sm:grid-cols-3 gap-3 mb-4">
 <div>
 <label className="label-mono block mb-1.5">Report type</label>
 <select className="input-field" value={uploadKind} onChange={e=>setUploadKind(e.target.value)} data-testid="inspection-kind">
 <option value="entry">Entry condition (move-in)</option>
 <option value="exit">Exit condition (move-out)</option>
 </select>
 </div>
 <div className="sm:col-span-2">
 <label className="label-mono block mb-1.5">Report date (optional)</label>
 <input type="date" className="input-field" value={reportDate} onChange={e=>setReportDate(e.target.value)} data-testid="inspection-date" />
 </div>
 </div>

 <div
 className="border-2 border-dashed border-[#E5E4DE] bg-[#FAF9F6] hover:border-[#1B6C62]/70 p-8 text-center cursor-pointer transition-colors"
 onClick={() => !uploading && fileRef.current?.click()}
 onDragOver={(e) => e.preventDefault()}
 onDrop={(e) => { e.preventDefault(); if (!uploading && e.dataTransfer.files[0]) upload(e.dataTransfer.files[0]); }}
 data-testid="inspection-dropzone"
 >
 {uploading ? (
 <>
 <Loader2 size={26} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-2 animate-spin" />
 <p className="font-serif text-base text-[#1A1A1A] mb-1">Reading your report…</p>
 <p className="text-xs text-[#6B7280]">Our AI is extracting every room and item. This can take up to a minute on long PDFs.</p>
 </>
 ) : (
 <>
 <Upload size={26} strokeWidth={1.5} className="mx-auto text-[#0D2B52] mb-2" />
 <p className="font-serif text-base text-[#1A1A1A] mb-1">Drop your condition report</p>
 <p className="text-xs text-[#6B7280]">PDF or photo of the form. We'll read every row.</p>
 </>
 )}
 <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden"
 onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="inspection-file-input" />
 </div>
 </div>

 {/* Reports list */}
 {loading && (
 <div className="space-y-3" data-testid="inspection-loading">
 <div className="skeleton h-16 w-full" />
 <div className="skeleton h-16 w-full" />
 </div>
 )}

 {!loading && reports.length === 0 && (
 <div className="card text-center py-12 bg-[#FAF9F6]" data-testid="inspections-empty">
 <ClipboardList size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-3 opacity-70" />
 <p className="font-serif text-lg text-[#0D2B52] mb-1">No condition reports yet</p>
 <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-4">
 Upload your entry or exit report above. Compass extracts every room and item row and
 pre-ticks anything that looks like a tenant disagreement.
 </p>
 <a href="/guide#inspections" className="text-xs text-[#1B6C62] hover:underline" data-testid="empty-inspections-guide-link">
 How inspections work →
 </a>
 </div>
 )}

 {reports.map((r) => (
 <InspectionReportCard key={r.id} report={r} onChange={load} onDelete={() => deleteReport(r.id)} />
 ))}
 </div>
 );
}

function SummaryStat({ label, value, tone }) {
 const colors = {
 navy: "text-[#0D2B52]",
 red: "text-[#B35D4C]",
 amber: "text-[#8A6D3B]",
 };
 return (
 <div>
 <div className="label-mono mb-1">{label}</div>
 <div className={`font-serif text-3xl ${colors[tone]}`} data-testid={`stat-${tone}`}>{value}</div>
 </div>
 );
}

function InspectionReportCard({ report, onChange, onDelete }) {
 const [expanded, setExpanded] = useState(true);
 const items = report.items || [];
 const disagreed = items.filter(i => i.tenant_disagreement).length;

 return (
 <div className="card" data-testid={`inspection-report-${report.id}`}>
 <div className="flex justify-between items-start mb-3">
 <button onClick={() => setExpanded(e => !e)} className="text-left flex items-start gap-2 group flex-1 min-w-0">
 {expanded ? <ChevronDown size={18} strokeWidth={1.5} className="text-[#6B7280] mt-0.5" /> : <ChevronRight size={18} strokeWidth={1.5} className="text-[#6B7280] mt-0.5" />}
 <div className="min-w-0 flex-1">
 <div className="label-mono mb-0.5">
 {report.kind === "entry" ? "ENTRY · MOVE-IN" : "EXIT · MOVE-OUT"}
 {report.report_date && <> · {report.report_date.slice(0,10)}</>}
 </div>
 <h3 className="font-serif text-lg text-[#0D2B52] group-hover:underline">
 {items.length} items · {disagreed} with disagreement
 </h3>
 </div>
 </button>
 <button onClick={onDelete} className="btn-ghost p-1.5 hover:text-[#B35D4C]" title="Delete report" data-testid={`delete-report-${report.id}`}>
 <Trash2 size={15} strokeWidth={1.5} />
 </button>
 </div>

 {expanded && (
 items.length === 0 ? (
 <p className="text-sm text-[#6B7280] italic text-center py-8">
 We couldn't pick up any rows from this report. Try a clearer scan, or contact us.
 </p>
 ) : (
 <div className="space-y-2 mt-3">
 {/* Group by room */}
 {Object.entries(items.reduce((acc, it) => {
 const k = it.room || ",";
 (acc[k] = acc[k] || []).push(it);
 return acc;
 }, {})).map(([room, roomItems]) => (
 <div key={room} className="border-t border-[#E5E4DE] pt-3">
 <div className="label-mono mb-2 text-[#0D2B52]">{room}</div>
 <div className="space-y-2">
 {roomItems.map(it => <InspectionItemRow key={it.id} item={it} onChange={onChange} />)}
 </div>
 </div>
 ))}
 </div>
 )
 )}
 </div>
 );
}

function InspectionItemRow({ item, onChange }) {
 const [editing, setEditing] = useState(false);
 const [disagree, setDisagree] = useState(!!item.tenant_disagreement);
 const [note, setNote] = useState(item.tenant_note || "");
 const [agentResp, setAgentResp] = useState(item.agent_response || "");
 const [saving, setSaving] = useState(false);

 const tags = item.tags || [];
 const raised = tags.includes("raised_with_agent");
 const resolved = tags.includes("resolved");
 const hasResponse = !!item.agent_response;

 const save = async (updates = {}) => {
 setSaving(true);
 try {
 const payload = {
 room: item.room,
 item: item.item,
 agent_condition: item.agent_condition || null,
 tenant_disagreement: updates.tenant_disagreement ?? disagree,
 tenant_note: (updates.tenant_note ?? note) || null,
 photo_document_ids: item.photo_document_ids || [],
 tags: updates.tags ?? item.tags ?? [],
 raised_at: updates.raised_at ?? item.raised_at ?? null,
 agent_response: (updates.agent_response ?? agentResp) || null,
 agent_responded_at: updates.agent_responded_at ?? item.agent_responded_at ?? null,
 };
 await api.put(`/inspection-items/${item.id}`, payload);
 await onChange();
 } catch (e) {
 alert("Couldn't save: " + (e.response?.data?.detail || e.message));
 } finally { setSaving(false); }
 };

 const toggleDisagree = async () => {
 const next = !disagree;
 setDisagree(next);
 await save({ tenant_disagreement: next });
 };

 const raise = async () => {
 setSaving(true);
 try {
 await api.post(`/inspection-items/${item.id}/raise`);
 await onChange();
 } catch (e) {
 alert("Couldn't save: " + (e.response?.data?.detail || e.message));
 } finally { setSaving(false); }
 };

 const saveResponse = async () => {
 const stamp = new Date().toISOString();
 const newTags = Array.from(new Set([...(item.tags || []), "agent_responded"]));
 await save({ agent_response: agentResp, agent_responded_at: stamp, tags: newTags });
 setEditing(false);
 };

 const markResolved = async () => {
 const newTags = Array.from(new Set([...(item.tags || []), "resolved"]));
 await save({ tags: newTags });
 };

 // Visual state colour
 const border =
 resolved ? "border-l-[#1B6C62]" :
 raised && !hasResponse ? "border-l-[#D4A24C]" :
 disagree && !raised ? "border-l-[#B35D4C]" :
 "border-l-[#E5E4DE]";

 return (
 <div className={`bg-white border border-[#E5E4DE] border-l-4 ${border} p-3`} data-testid={`item-${item.id}`}>
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="font-medium text-[#1A1A1A] text-sm">{item.item}</div>
 {item.agent_condition && (
 <div className="text-xs text-[#6B7280] mt-0.5 italic">Agent recorded: "{item.agent_condition}"</div>
 )}
 </div>

 <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer flex-shrink-0" data-testid={`disagree-toggle-${item.id}`}>
 <input type="checkbox" checked={disagree} onChange={toggleDisagree} disabled={saving} className="accent-[#B35D4C]" />
 <span className={disagree ? "text-[#B35D4C] font-medium" : "text-[#6B7280]"}>I disagree</span>
 </label>
 </div>

 {disagree && (
 <div className="mt-3 pl-2 border-l border-[#E5E4DE] space-y-2.5">
 {/* Tenant note */}
 <div>
 <label className="label-mono block mb-1">My note</label>
 <textarea
 className="input-field text-sm" rows={2}
 placeholder="What's wrong with this. what should it really say?"
 value={note}
 onChange={e => setNote(e.target.value)}
 onBlur={() => note !== (item.tenant_note || "") && save({ tenant_note: note })}
 data-testid={`note-${item.id}`}
 />
 </div>

 {/* Status pills + actions */}
 <div className="flex flex-wrap items-center gap-2">
 {raised ? (
 <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 bg-[#FAF7F0] text-[#8A6D3B] border border-[#D4A24C]/40">
 <CheckCircle2 size={11} strokeWidth={2} /> Raised {item.raised_at?.slice(0,10)}
 </span>
 ) : (
 <button onClick={raise} disabled={saving}
 className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 bg-[#B35D4C] text-white hover:bg-[#9d4d3e] disabled:opacity-50"
 data-testid={`raise-${item.id}`}>
 <Send size={11} strokeWidth={2} /> Mark raised with agent
 </button>
 )}

 {hasResponse && (
 <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 bg-[#E6F5F2] text-[#1B6C62] border border-[#1B6C62]/30">
 <MessageSquare size={11} strokeWidth={2} /> Agent replied
 </span>
 )}

 {resolved && (
 <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 bg-[#1B6C62] text-white">
 <CheckCircle2 size={11} strokeWidth={2} /> Resolved
 </span>
 )}

 {raised && !resolved && (
 <button onClick={() => setEditing(e => !e)}
 className="btn-ghost text-[11px] inline-flex items-center gap-1"
 data-testid={`edit-response-${item.id}`}>
 <MessageSquare size={11} strokeWidth={1.5} /> {hasResponse ? "Edit agent's reply" : "Log agent's reply"}
 </button>
 )}

 {raised && hasResponse && !resolved && (
 <button onClick={markResolved} className="btn-ghost text-[11px] inline-flex items-center gap-1 text-[#1B6C62]" data-testid={`resolve-${item.id}`}>
 <CheckCircle2 size={11} strokeWidth={1.5} /> Mark resolved
 </button>
 )}
 </div>

 {/* Agent response editor */}
 {editing && raised && (
 <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-2.5 mt-1">
 <label className="label-mono block mb-1">Agent's response</label>
 <textarea
 className="input-field text-sm" rows={2}
 placeholder="Paste what the agent said in reply…"
 value={agentResp}
 onChange={e => setAgentResp(e.target.value)}
 data-testid={`response-${item.id}`}
 />
 <div className="flex justify-end gap-2 mt-2">
 <button onClick={() => setEditing(false)} className="btn-ghost text-xs">Cancel</button>
 <button onClick={saveResponse} disabled={saving} className="btn-primary text-xs" data-testid={`save-response-${item.id}`}>Save</button>
 </div>
 </div>
 )}

 {hasResponse && !editing && (
 <div className="text-xs text-[#333] font-serif bg-[#FAF9F6] border border-[#E5E4DE] p-2.5">
 <div className="label-mono mb-1">Agent said</div>
 "{item.agent_response}"
 {item.agent_responded_at && <span className="block mt-1 text-[10px] font-mono text-[#6B7280]">, {item.agent_responded_at.slice(0,10)}</span>}
 </div>
 )}
 </div>
 )}
 </div>
 );
}
